import { useDevicesContext } from "@hireroo/app-helper/hooks";
import { RemoteUser, useAgoraIsConnected, useAgoraJoin } from "@hireroo/app-helper/livecoding/agora";
import { Auth } from "@hireroo/app-store/essential/employee";
import { RemotesId } from "@hireroo/app-store/page/e/remotes_id";
import { Snackbar } from "@hireroo/app-store/widget/shared/Snackbar";
import * as Graphql from "@hireroo/graphql/client/urql";
import { useTranslation } from "@hireroo/i18n";
import { Widget } from "@hireroo/presentation";
import * as Sentry from "@sentry/react";
import * as React from "react";

import LiveCodingDeviceSettingsContainer from "../../../../../../widget/v2/shared/LiveCodingDeviceSettings/Container";
import LiveCodingVideoViewContainer from "../../../../../../widget/v2/shared/LiveCodingVideoView/Container";
import LiveCodingVideoChatPopperContainer from "../LiveCodingVideoChatPopper/Container";
import { useGenerateDevicePermissionState } from "./useGenerateDevicePermissionState";

export type GenerateLiveCodingPreJoinScreenPropsArgs = {
  videoChatUIStatus: "ENTER" | "EXIT";
  exitVideoChat: () => void;
};

export const useGenerateProps = (args: GenerateLiveCodingPreJoinScreenPropsArgs): Widget.LiveCodingPreJoinScreenProps => {
  const { t } = useTranslation();
  const { videoChatUIStatus, exitVideoChat } = args;
  const liveCodingId = RemotesId.useLiveCodingId();
  const uid = Auth.useCurrentUid();
  const user = RemotesId.useEmployeeUser(uid ?? "");
  const isConnected = useAgoraIsConnected();
  const videoChatJoinStatus = React.useMemo(() => {
    return isConnected ? "IN_CALL" : "PREVIEW";
  }, [isConnected]);
  const { initializeDevices, cleanupDevices, toggleVideo, toggleAudio, isVideoEnabled, isAudioEnabled, devicesStatus } = useDevicesContext();
  const cleanUpRef = React.useRef<() => void>();
  const [generateRtcTokenResult, generateRtcToken] = Graphql.useGenerateLiveCodingRtcTokenForEmployeeMutation();

  useAgoraJoin(
    {
      appid: import.meta.env.VITE_AGORA_APP_ID,
      channel: generateRtcTokenResult.data?.generateLiveCodingRtcToken.channelName ?? "",
      token: generateRtcTokenResult.data?.generateLiveCodingRtcToken.token ?? "",
      uid: generateRtcTokenResult.data?.generateLiveCodingRtcToken.voiceId ?? "",
    },
    generateRtcTokenResult.data !== undefined,
  );

  const { permissionState } = useGenerateDevicePermissionState({ opened: videoChatUIStatus === "ENTER" });

  const handleClickJoin = React.useCallback(() => {
    generateRtcToken({
      input: {
        liveCodingId: liveCodingId,
        participantId: uid,
        participantType: "INTERVIEWER",
      },
    }).catch(err => {
      Snackbar.notify({
        severity: "error",
        message: t("ビデオ通話の接続に失敗しました。画面をリロードしてから再度お試しください。"),
      });
      Sentry.captureException(err);
    });
  }, [generateRtcToken, liveCodingId, t, uid]);

  const localUser = React.useMemo((): RemoteUser => {
    return {
      uid: user.userId,
      hasAudio: isAudioEnabled,
      hasVideo: isVideoEnabled,
    };
  }, [user.userId, isAudioEnabled, isVideoEnabled]);

  React.useEffect(() => {
    // デバイスへのアクセスが拒否されている場合はスキップ
    if (permissionState === "denied") {
      return;
    }

    // デバイスへのアクセスが許可されていない場合はパーミッションを要求
    if (permissionState === "prompt") {
      const requestPermission = async () => {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      };
      requestPermission();
      return;
    }

    // デバイスへのアクセスが許可されている場合はデバイスを初期化
    if (permissionState === "granted") {
      initializeDevices();
      cleanUpRef.current = () => {
        cleanupDevices();
      };
    }

    return () => {
      if (cleanUpRef.current) {
        cleanUpRef.current();
        cleanUpRef.current = undefined;
      }
    };
  }, [cleanupDevices, initializeDevices, permissionState, t]);

  React.useEffect(() => {
    if (devicesStatus === "ERROR") {
      Snackbar.notify({
        severity: "error",
        message: t("デバイスの設定に失敗しました。画面をリロードしてから再度お試しください。"),
      });
    }
  }, [devicesStatus, t]);

  return {
    previewDialog: {
      isLoading: false,
      open: videoChatUIStatus === "ENTER" && videoChatJoinStatus === "PREVIEW",
      VideoContent: <LiveCodingVideoViewContainer user={localUser} isLocal={true} />,
      deviceToggleButtons: {
        audioToggleButton: {
          isEnabled: isAudioEnabled,
          onClick: () => {
            toggleAudio();
          },
        },
        videoToggleButton: {
          isEnabled: isVideoEnabled,
          onClick: () => {
            toggleVideo();
          },
        },
      },
      yesButton: {
        loading: devicesStatus === "LOADING",
        disabled: permissionState !== "granted" || devicesStatus === "IDLE" || devicesStatus === "ERROR",
        onClick: () => {
          handleClickJoin();
        },
      },
      noButton: {
        onClick: () => {
          exitVideoChat();
        },
      },
      permissionState: permissionState ?? "prompt",
      DeviceSettings: permissionState === "granted" && <LiveCodingDeviceSettingsContainer />,
      forceClose: isConnected,
    },
    status: videoChatJoinStatus,
    VideoChat: <LiveCodingVideoChatPopperContainer />,
  };
};
